import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { useUserMessages, UserMessage } from '../../hooks/useUserMessages';

interface MessageTypeConfig {
  icon: string;
  color: string;
  bg: string;
  label: string;
  /** Where clicking the notification should take the user, if anywhere. */
  route?: string;
}

const MESSAGE_TYPE_CONFIG: Record<string, MessageTypeConfig> = {
  PRO_ALERT: { icon: '🎯', color: '#6366f1', bg: 'rgba(99, 102, 241, 0.12)', label: 'Signal Pro', route: '/technical-alerts' },
  MARKET_SENTIMENT: { icon: '🌡️', color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.12)', label: 'Sentiment du marché', route: '/analysis' },
  BEHAVIORAL_ALERT: { icon: '🧠', color: '#ec4899', bg: 'rgba(236, 72, 153, 0.12)', label: 'Comportemental', route: '/portfolio-analysis' },
  // NB: "ALERT" est utilisé côté backend à la fois pour les alertes de prix déclenchées
  // (tasks.py::run_price_alert_check) ET pour des notifications sans rapport (ex: activation
  // de code promo, crud.py). Un routage automatique vers /settings/alerts serait donc parfois
  // faux (ex: "Code Promo Activé" enverrait sur la page des alertes de prix) — pas de route
  // tant que ces deux usages ne sont pas distingués côté backend par un type dédié.
  ALERT: { icon: '🔔', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)', label: 'Alerte' },
  SYSTEM: { icon: '⚙️', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.12)', label: 'Système' },
  INFO: { icon: 'ℹ️', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.12)', label: 'Info' },
  ANNOUNCEMENT: { icon: '📣', color: '#059669', bg: 'rgba(5, 150, 105, 0.12)', label: 'Annonce' },
  FORUM: { icon: '💬', color: '#0891b2', bg: 'rgba(8, 145, 178, 0.12)', label: 'Forum', route: '/forum' },
  SUCCESS: { icon: '🎉', color: '#16a34a', bg: 'rgba(22, 163, 74, 0.12)', label: 'Récompense' },
};

const DEFAULT_TYPE_CONFIG: MessageTypeConfig = { icon: '📩', color: 'var(--text-muted)', bg: 'var(--bg-input)', label: 'Message' };

const getTypeConfig = (type: string, source?: string): MessageTypeConfig => {
  if (source === 'broadcast' && !MESSAGE_TYPE_CONFIG[type]) {
    return { icon: '📢', color: '#059669', bg: 'rgba(5, 150, 105, 0.12)', label: 'Annonce' };
  }
  return MESSAGE_TYPE_CONFIG[type] || DEFAULT_TYPE_CONFIG;
};

const formatRelativeTime = (dateStr: string) => {
  const d = new Date(dateStr);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  if (diffHours < 24) return `il y a ${diffHours} h`;
  if (diffDays === 1) return 'hier';
  if (diffDays < 7) return `il y a ${diffDays} j`;

  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatFullDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

type FilterTab = 'ALL' | 'UNREAD';

const MessagesScreen: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { messages, total, hasMore, loading, refetch, loadMore } = useUserMessages();
  const [unreadCount, setUnreadCount] = useState(0);
  const [tab, setTab] = useState<FilterTab>('ALL');

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await fetch(`${API_URL}/v1/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setUnreadCount(data.unread_count);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    if (token) fetchUnreadCount();
  }, [token, messages]);

  const handleMarkAsRead = async (messageId: number | string) => {
    // Skip for broadcast messages (they have string IDs like "bc_123")
    if (typeof messageId === 'string') return;

    try {
      const response = await fetch(`${API_URL}/v1/messages/${messageId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) refetch();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/messages/read-all`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        refetch();
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    }
  };

  const handleDeleteMessage = async (messageId: number | string) => {
    // Skip for broadcast messages
    if (typeof messageId === 'string') return;

    try {
      const response = await fetch(`${API_URL}/v1/messages/${messageId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) refetch();
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleOpenMessage = (msg: UserMessage) => {
    const cfg = getTypeConfig(msg.message_type, msg.source);
    if (!msg.is_read) handleMarkAsRead(msg.id);
    if (cfg.route) navigate(cfg.route);
  };

  const filteredMessages = tab === 'UNREAD' ? messages.filter((m) => !m.is_read) : messages;
  const loadedUnreadCount = messages.filter((m) => !m.is_read).length;
  const hasHiddenUnread = tab === 'UNREAD' && hasMore && loadedUnreadCount < unreadCount;

  return (
    <SiteLayout>
      <Header icon='email' title='Messages' />
      <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
        {/* Unread Count Banner */}
        {unreadCount > 0 && (
          <div
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '15px 20px',
              borderRadius: '12px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 4px 14px rgba(102, 126, 234, 0.35)',
            }}
          >
            <span style={{ fontSize: '24px' }}>📬</span>
            <span>
              Vous avez <strong>{unreadCount}</strong> message{unreadCount > 1 ? 's' : ''} non lu
              {unreadCount > 1 ? 's' : ''}
            </span>
          </div>
        )}

        <Box>
          <div
            className='box-title box-vertical-padding box-horizontal-padding'
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {(['ALL', 'UNREAD'] as FilterTab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  style={{
                    border: 'none',
                    background: tab === t ? 'var(--accent-color, #764ba2)' : 'transparent',
                    color: tab === t ? 'white' : 'var(--text-main)',
                    fontWeight: 600,
                    fontSize: '13px',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {t === 'ALL' ? `Toutes (${total})` : `Non lues (${unreadCount})`}
                </button>
              ))}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-color, #764ba2)',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--bg-hover)')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                ✔️ Tout marquer comme lu
              </button>
            )}
          </div>
          <div className='box-content'>
            {loading ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>
                <div style={{ fontSize: '40px', marginBottom: '15px' }}>⏳</div>
                Chargement des messages...
              </div>
            ) : filteredMessages.length === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>{tab === 'UNREAD' ? '✅' : '📭'}</div>
                <p style={{ fontSize: '18px', marginBottom: '5px' }}>
                  {tab === 'UNREAD' ? 'Tout est lu !' : 'Aucun message'}
                </p>
                {tab === 'ALL' && (
                  <p style={{ fontSize: '14px' }}>
                    Vos notifications d'alertes et de signaux apparaîtront ici
                  </p>
                )}
              </div>
            ) : (
              <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {filteredMessages.map((msg) => {
                  const cfg = getTypeConfig(msg.message_type, msg.source);
                  const clickable = !!cfg.route;
                  return (
                    <div
                      key={msg.id}
                      onClick={clickable ? () => handleOpenMessage(msg) : undefined}
                      style={{
                        display: 'flex',
                        gap: '14px',
                        alignItems: 'flex-start',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)',
                        borderLeft: `3px solid ${msg.is_read ? 'var(--border-color)' : cfg.color}`,
                        background: msg.is_read ? 'transparent' : 'var(--bg-hover)',
                        cursor: clickable ? 'pointer' : 'default',
                        transition: 'transform 0.12s, box-shadow 0.12s',
                      }}
                      onMouseOver={(e) => {
                        if (!clickable) return;
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'none';
                      }}
                    >
                      <div
                        style={{
                          flexShrink: 0,
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: cfg.bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                        }}
                      >
                        {cfg.icon}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                          <strong
                            style={{
                              fontSize: '14px',
                              color: msg.is_read ? 'var(--text-main)' : 'var(--text-heading)',
                            }}
                          >
                            {msg.title}
                          </strong>
                          {!msg.is_read && (
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />
                          )}
                          <span
                            style={{
                              background: cfg.bg,
                              color: cfg.color,
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                            }}
                          >
                            {cfg.label}
                          </span>
                        </div>
                        <p style={{ margin: '0 0 6px 0', color: 'var(--text-main)', fontSize: '13px', lineHeight: '1.5' }}>
                          {msg.content}
                        </p>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }} title={formatFullDate(msg.created_at)}>
                          {formatRelativeTime(msg.created_at)}
                        </span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                        {!msg.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(msg.id)}
                            style={{
                              background: 'var(--bg-input)',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              color: 'var(--text-main)',
                            }}
                            title='Marquer comme lu'
                          >
                            ✓
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteMessage(msg.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '15px',
                            color: 'var(--text-muted)',
                            padding: '5px',
                          }}
                          title='Supprimer'
                        >
                          🗑️
                        </button>
                        {clickable && (
                          <span style={{ color: 'var(--text-muted)', fontSize: '16px', paddingLeft: '2px' }}>›</span>
                        )}
                      </div>
                    </div>
                  );
                })}

                {hasHiddenUnread && (
                  <div style={{ textAlign: 'center', padding: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                    {unreadCount - loadedUnreadCount} message{unreadCount - loadedUnreadCount > 1 ? 's' : ''} non lu
                    {unreadCount - loadedUnreadCount > 1 ? 's' : ''} supplémentaire{unreadCount - loadedUnreadCount > 1 ? 's' : ''}, plus ancien{unreadCount - loadedUnreadCount > 1 ? 's' : ''} — chargez plus de messages pour {unreadCount - loadedUnreadCount > 1 ? 'les' : 'le'} voir.
                  </div>
                )}

                {hasMore && (
                  <button
                    onClick={loadMore}
                    style={{
                      margin: '4px auto 0',
                      display: 'block',
                      background: 'var(--bg-input)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '20px',
                      padding: '8px 20px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'var(--text-main)',
                    }}
                  >
                    Charger plus de messages ({total - messages.length} restants)
                  </button>
                )}
              </div>
            )}
          </div>
        </Box>
      </div>
    </SiteLayout>
  );
};

export default MessagesScreen;
